import { permissionLevel, rolesAtOrAbove } from 'cesg-shared';
import { PermissionType, UserRole } from 'cesg-shared/dist/models';

/**
 * App authorization policy, deliberately outside the file manager (D6): this maps
 * `PermissionType` × `UserRole` — two `cesg-shared` enums — onto capability strings, which is a
 * statement about who may do what in this organisation, not about how a component renders.
 *
 * A host computes these per project and stamps them onto each mount it hands down. Inside the file
 * manager the eight strings are opaque tokens; only `permissions/modes.ts` interprets them, and only
 * into UI states.
 */
export type FileManagerPermission =
  | 'files:read'
  | 'files:upload'
  | 'folders:upload'
  | 'files:move'
  | 'files:copy'
  | 'files:preview'
  | 'files:delete'
  | 'files:rename'
  | 'tool:outlook-mail-copy'
  | 'tool:onedrive-sync';

/**
 * The `tool:` half of the union — every entry in the file manager's tool registry is gateable by
 * construction, because a `FileManagerTool.permission` typed as this cannot name anything else.
 *
 * A separate namespace from `files:` on purpose. What is gated here is the TOOL, not file access:
 * a user who can see a file can already download it and attach it to a mail by hand, so
 * `tool:outlook-mail-copy` grants no new reach into content — it grants the convenience of doing it in
 * bulk. Filing it under `files:` would imply the permission table protects a read it does not protect.
 */
export type ToolPermission = Extract<FileManagerPermission, `tool:${string}`>;

interface CapabilityRule {
  minLevel: PermissionType;
  roles: UserRole[];
}

// The role axis carries exactly ONE distinction: `tool:` capabilities are internal-only. Every
// `files:`/`folders:` capability qualifies from GUEST upward, and `PermissionType` alone decides who
// gets it — an external party invited into a project with `files: EDIT` is someone the org has
// decided may edit, and the SHARED library's ACL already says so (`GUEST_EDIT` is in its `grants`).
// Refusing the write in the UI while SharePoint permits it made the app the odd one out.
//
// Keep that shape when adding a rule: reach for a narrower `roles` array only for a capability that
// is genuinely about WHO someone is rather than what they may do, the way the mail tool is.
const CAPABILITY_RULES: Record<FileManagerPermission, CapabilityRule> = {
  'files:read': {
    minLevel: PermissionType.VIEW,
    roles: rolesAtOrAbove(UserRole.GUEST),
  },
  'folders:upload': {
    minLevel: PermissionType.EDIT,
    roles: rolesAtOrAbove(UserRole.GUEST),
  },
  'files:upload': {
    minLevel: PermissionType.EDIT,
    roles: rolesAtOrAbove(UserRole.GUEST),
  },
  'files:move': {
    minLevel: PermissionType.EDIT,
    roles: rolesAtOrAbove(UserRole.GUEST),
  },
  // Same values as files:move today, so no tier's behaviour changes — but a distinct capability, not
  // a synonym. A move relocates within one library's ACL and is reversible by dragging back; a copy
  // creates a second copy under a DIFFERENT ACL, and toward the shared library that is an
  // irreversible disclosure to external parties. Splitting them now is free; splitting them after
  // the fact would be a permission change on a shipped feature.
  //
  // That disclosure reasoning is why this one was the last to open to GUEST, and why it is safe to:
  // the crossing it guards is one a guest cannot perform. The CESG library grants no GUEST_* group
  // (see SITE_LIBRARIES), so a guest's table only ever holds the shared mount and the only copy they
  // can reach is an in-place duplicate. Should a future library ever be granted to guests, this is
  // the rule to revisit first.
  'files:copy': {
    minLevel: PermissionType.EDIT,
    roles: rolesAtOrAbove(UserRole.GUEST),
  },
  'files:preview': {
    minLevel: PermissionType.VIEW,
    roles: rolesAtOrAbove(UserRole.GUEST),
  },
  'files:delete': {
    minLevel: PermissionType.EDIT,
    roles: rolesAtOrAbove(UserRole.GUEST),
  },
  'files:rename': {
    minLevel: PermissionType.EDIT,
    roles: rolesAtOrAbove(UserRole.GUEST),
  },
  // Appended last deliberately: computeFileManagerPermissions() iterates Object.keys, so the granted
  // array's order follows this table, and inserting mid-table would churn every fixture asserting on
  // it with toEqual.
  //
  // VIEW, not EDIT: reading is all the tool needs, and EDIT would be the wrong axis — a read-only
  // internal member is still someone you would trust to email drawings to a subcontractor.
  //
  // Role-gated to internal staff because the population that should not have mass-export convenience
  // is the external one. That also removes a guaranteed runtime failure: guests here are temporary
  // Entra accounts with no Exchange mailbox, so `POST /me/messages` would come back 404
  // MailboxNotEnabledForRESTAPI for every one of them.
  //
  // The only rule left with a narrower `roles` than GUEST, and deliberately so: it is a statement
  // about who someone IS, not about what their `files` level permits them to do to content.
  'tool:outlook-mail-copy': {
    minLevel: PermissionType.VIEW,
    roles: rolesAtOrAbove(UserRole.CESG),
  },
  'tool:onedrive-sync': {
    minLevel: PermissionType.VIEW,
    roles: rolesAtOrAbove(UserRole.CESG),
  },
};

const isSpecialRole = (role: UserRole) => role === UserRole.ADMIN;

export const computeFileManagerPermissions = (
  level: PermissionType,
  role: UserRole,
): FileManagerPermission[] =>
  (Object.keys(CAPABILITY_RULES) as FileManagerPermission[]).filter((key) => {
    const rule = CAPABILITY_RULES[key];
    return (
      isSpecialRole(role) ||
      (permissionLevel(level) >= permissionLevel(rule.minLevel) &&
        rule.roles.includes(role))
    );
  });

/**
 * Whether a capability set can browse a mount at all — the gate a host applies before putting the
 * mount in the table it hands down.
 *
 * Lives here rather than as a `computeViewMode` inside the file manager, because it is no longer a
 * rendering decision: a mount the user cannot read simply is not listed, and a host with nothing left
 * to list renders its own "no access" notice instead of the pane (D6). That also means the app-level
 * `files` gate cannot be bypassed by a user whose SharePoint role family would have let the drive read
 * succeed anyway.
 */
export const canBrowse = (
  capabilities: FileManagerPermission[],
): boolean => capabilities.includes('files:read');

export const canSyncToOneDrive = (
  capabilities: FileManagerPermission[],
): boolean => capabilities.includes('tool:onedrive-sync');
